import type { ProductEntity } from '@/entities/assortment'
import type { InterpretationStepModel } from '@/entities/optimization-statistic'
import type { TFunction } from 'i18next'
import type { UiGroupType } from '../model/types'

import React, { useEffect, useMemo } from 'react'
import { find } from 'lodash/fp'
import { AlertTriangleIcon, ChevronUpIcon, LockIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	InterpretationContent,
	useFieldTranslationsResolver,
} from '@/features/interpretation'

import { useOptimizationQuery } from '@/entities/optimization'
import { useInterpretationQuery } from '@/entities/optimization-statistic'
import {
	getAlertVariant,
	usePricingAlertsAnnotationsQuery,
} from '@/entities/price-alert'
import { Access } from '@/entities/session'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import FallbackError from '@/shared/components/placeholders/FallbackError'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { ampli } from '@/shared/lib/ampli'
import { useStore } from '@/shared/lib/datasheet/store'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { getGroupSteps, getPriceLabel, isChanged } from '../lib/helpers'
import { InterpretationStages } from '../model/types'

function useInterpretation({
	line_id,
	optimization_group_id,
}: {
	line_id: string
	optimization_group_id: string
}) {
	const { data: optimization } = useOptimizationQuery(optimization_group_id)
	const optimization_id = optimization?.id

	useEffect(() => {
		ampli.productsInterpretation({
			Engine: 'ml',
		})
	}, [line_id, optimization_id])

	return useInterpretationQuery(
		{
			optimization_id: optimization_id!,
			optimization_group_id,
			line_id,
		},
		{
			enabled: !!optimization_id,
			retry: false,
		}
	)
}
const getInterpretationUiGroups = (
	interpretation: InterpretationStepModel[],
	t: TFunction
) =>
	getInterpretationUiGroupsConfig(t).map(({ key, title }) => {
		const groupSteps = getGroupSteps(interpretation || [], key)
		const lastStepPriceRange = groupSteps[groupSteps.length - 1]?.price_label
		const finalPrice = lastStepPriceRange
			? lastStepPriceRange[lastStepPriceRange.length - 1]
			: null

		return {
			key,
			title,
			groupSteps,
			finalPrice: key === InterpretationStages.SETUP ? null : finalPrice,
		}
	})
export default function Interpretation({
	content,
}: {
	content: ProductEntity
}) {
	const { t } = useTranslation()

	const localData = useStore((state) => state.localData)
	const line_id = content?.line_id
	const {
		data: interpretation,
		error,
		isError,
		isLoading,
	} = useInterpretation({
		line_id,
		optimization_group_id: content.optimization_group_id,
	})
	let stepNumber = 0
	const interpretationUiGroups: UiGroupType[] = useMemo(
		() => getInterpretationUiGroups(interpretation || [], t),
		[interpretation, t]
	)
	if (isError) {
		return (
			<Empty>
				<EmptyHeader>
					<EmptyMedia variant='icon' className='text-destructive'>
						<AlertTriangleIcon className='text-destructive' />
					</EmptyMedia>
					<EmptyTitle>
						{t('interpretation.error', {
							defaultValue: 'Error while fetching interpretation',
						})}
					</EmptyTitle>
					<EmptyDescription>
						{t('interpretation.error_description', {
							defaultValue:
								'Please try again later or contact support if the problem persists.',
						})}
					</EmptyDescription>
				</EmptyHeader>
				<Access roles={['SuperUser']}>
					<EmptyContent>
						<code className='rounded-md bg-muted p-2 text-xs'>
							{error.message}
						</code>
						<p className='inline-flex items-center gap-2 text-xs text-muted-foreground'>
							<LockIcon className='size-3' />
							Visible only for SuperUser
						</p>
					</EmptyContent>
				</Access>
			</Empty>
		)
	}
	if (isLoading) {
		return (
			<div className='flex w-full flex-col gap-2'>
				<div className='flex items-center gap-2'>
					<Skeleton className='size-4' />
					<Skeleton className='h-4 w-42' />
				</div>
				<div className='flex w-full flex-col gap-2 pl-6'>
					<Skeleton className='h-4 w-20' />
					<Skeleton className='h-18 w-full' />
				</div>
				<div className='mt-6 flex items-center gap-2 opacity-50'>
					<Skeleton className='size-4' />
					<Skeleton className='h-4 w-42' />
				</div>
				<div className='flex w-full flex-col gap-2 pl-6 opacity-50'>
					<Skeleton className='h-4 w-20' />
					<Skeleton className='h-18 w-full' />
				</div>
				<div className='mt-6 flex items-center gap-2 opacity-25'>
					<Skeleton className='size-4' />
					<Skeleton className='h-4 w-42' />
				</div>
				<div className='flex w-full flex-col gap-2 pl-6 opacity-25'>
					<Skeleton className='h-4 w-20' />
					<Skeleton className='h-18 w-full' />
				</div>
			</div>
		)
	}
	const isDataChanged = isChanged(localData?.[line_id], content)
	const finalPriceStatus = find(
		{ ui_group: InterpretationStages.FINAL_RESULT },
		interpretation
	)

	return (
		<div
			className={cn('rounded-lg border bg-secondary/30 p-4', {
				'opacity-50': isDataChanged,
			})}
		>
			<div className='relative overflow-hidden'>
				<ErrorBoundary fallback={FallbackError}>
					{interpretationUiGroups?.map(({ groupSteps, title, finalPrice }) =>
						groupSteps.length ? (
							<TimelineItem key={title}>
								<Collapsible className='w-full overflow-hidden' defaultOpen>
									<div className='flex items-center justify-between space-x-2 align-bottom'>
										<div className='flex w-full items-center font-semibold'>{`${++stepNumber}.${title}`}</div>
										<CollapsibleTrigger asChild>
											<Button
												variant='ghost'
												size='icon-sm'
												className='data-[state=closed]:rotate-180'
											>
												<ChevronUpIcon className='flex size-4 cursor-pointer items-center' />
											</Button>
										</CollapsibleTrigger>
									</div>
									<div className='flex h-fit w-full items-center justify-between'>
										<p className='text-sm text-muted-foreground'>
											{`${t('interpretation.price_steps', { defaultValue: 'Price steps' })}:`}{' '}
											<strong>{groupSteps.length}</strong>
										</p>
										{finalPrice && (
											<p className='text-sm font-bold'>
												{formatNumber(finalPrice, NumberFormats.Fixed)}
											</p>
										)}
									</div>
									<CollapsibleContent className='w-full'>
										<div className='mt-2 w-full space-y-2'>
											{groupSteps.map(
												(
													{ children, ...props }: InterpretationStepModel,
													index: number
												) => {
													const hasContent = !!children?.length

													return hasContent ? (
														<InterpretationStepContentCard
															key={index}
															{...props}
															contextParams={{
																optimization_group_id:
																	content.optimization_group_id,
															}}
														>
															{children}
														</InterpretationStepContentCard>
													) : (
														<InterpretationStepTextCard
															key={index}
															{...props}
														/>
													)
												}
											)}
										</div>
									</CollapsibleContent>
								</Collapsible>
							</TimelineItem>
						) : null
					)}
				</ErrorBoundary>
			</div>
			<div className='flex gap-2'>
				<div className='z-10 aspect-[1/1] size-4 rounded-full border-4 border-primary bg-primary-50 dark:bg-background' />
				{finalPriceStatus ? (
					<div className='flex w-full flex-col gap-1'>
						<div className='flex justify-between space-x-2'>
							<p className='overflow-hidden text-base leading-4 font-semibold break-words'>
								{t(finalPriceStatus.title, {
									defaultValue: finalPriceStatus.title,
								})}
							</p>
							{finalPriceStatus.price_label && (
								<p className='text-base leading-4 font-semibold text-nowrap'>
									{getPriceLabel(finalPriceStatus.price_label)}
								</p>
							)}
						</div>
						{finalPriceStatus.subtitle && (
							<p className='text-sm break-words text-muted-foreground'>
								{t(finalPriceStatus.subtitle, {
									defaultValue: finalPriceStatus.subtitle,
								})}
							</p>
						)}
					</div>
				) : (
					<div className='flex flex-col gap-1'>
						<p className='overflow-hidden text-base leading-4 font-semibold break-words'>
							{t('interpretation.empty.title')}
						</p>
						<p className='text-sm break-words text-muted-foreground'>
							{t('interpretation_empty')}
						</p>
					</div>
				)}
			</div>
		</div>
	)
}

const getInterpretationUiGroupsConfig = (t: TFunction) => [
	{
		key: InterpretationStages.SETUP,
		title: t('interpretation.stage.setup_stage', {
			defaultValue: 'Setup Stage',
		}),
	},
	{
		key: InterpretationStages.PRICE_OPTIMIZATION,
		title: t('interpretation.stage.price_optimization', {
			defaultValue: 'Price optimization',
		}),
	},
	{
		key: InterpretationStages.PRICE_ADJUSTMENT,
		title: t('interpretation.stage.price_adjustment', {
			defaultValue: 'Price Adjustment',
		}),
	},
	{
		key: InterpretationStages.MANUALLY_CHANGED,
		title: t('interpretation.stage.manually_changed', {
			defaultValue: 'Manually changed',
		}),
	},
]
function TimelineItem({ children }: { children: React.ReactNode }) {
	return (
		<>
			<div className='absolute left-[7px] z-0 mt-2 h-full w-[2px] bg-primary' />
			<div className='mb-5 flex items-baseline space-x-2'>
				<div className='z-10 aspect-[1/1] size-4 translate-y-[3px] rounded-full border-4 border-primary bg-primary-50 dark:bg-background' />
				{children}
			</div>
		</>
	)
}
function InterpretationStepTextCard({
	title,
	subtitle,
	price_label,
	alerts,
	data,
}: Partial<InterpretationStepModel>) {
	const { t } = useTranslation()

	const resolveFieldTranslations = useFieldTranslationsResolver(data!)

	return (
		<div className='space-y-1 rounded-lg border bg-card px-4 py-2'>
			<p className='text-sm font-semibold'>
				{t(title!, { defaultValue: title! })}
			</p>
			<div className='flex justify-between space-x-1'>
				{subtitle && (
					<div className='space-y-1'>
						{!!alerts?.length && <AlertsContainer alerts={alerts} />}
						<p className='text-sm text-muted-foreground'>
							{resolveFieldTranslations([subtitle])}
						</p>
					</div>
				)}
				{!!price_label?.length && (
					<p className='ml-auto text-sm font-bold'>
						{getPriceLabel(price_label)}
					</p>
				)}
			</div>
		</div>
	)
}
function InterpretationStepContentCard({
	title,
	subtitle,
	price_label,
	children,
	alerts,
	data,
	contextParams,
}: InterpretationStepModel & {
	contextParams: { optimization_group_id: string }
}) {
	const { t } = useTranslation()

	const resolveFieldTranslations = useFieldTranslationsResolver(data!)

	return (
		<div className='w-full overflow-hidden rounded-lg border bg-card px-4 py-2 text-sm'>
			<Collapsible defaultOpen className='space-y-1 overflow-hidden'>
				<div className='flex items-center justify-between space-x-2 align-bottom'>
					<div className='flex w-full items-center font-semibold'>
						{t(title, { defaultValue: title })}
					</div>
					<CollapsibleTrigger asChild>
						<Button
							variant='ghost'
							size='icon-sm'
							className='data-[state=closed]:rotate-180'
						>
							<ChevronUpIcon className='flex size-4 cursor-pointer items-center' />
						</Button>
					</CollapsibleTrigger>
				</div>
				<div className='grid w-full gap-1'>
					{!!alerts?.length && <AlertsContainer alerts={alerts} />}
					<div className='flex w-full items-center justify-between space-x-2 overflow-hidden'>
						{subtitle && (
							<p className='overflow-hidden text-sm break-words text-muted-foreground'>
								{resolveFieldTranslations([subtitle])}
							</p>
						)}
						{!!price_label?.length && (
							<p className='ml-auto font-bold'>{getPriceLabel(price_label)}</p>
						)}
					</div>
				</div>
				<CollapsibleContent className='mt-2 border-t py-2'>
					<InterpretationContent
						data={data}
						params={contextParams}
						{...(children ? { children } : {})}
					/>
				</CollapsibleContent>
			</Collapsible>
		</div>
	)
}
function AlertsContainer({ alerts }: { alerts: string[] }) {
	const { t } = useTranslation()

	const { isLoading, data: alertAnotations } =
		usePricingAlertsAnnotationsQuery()

	return (
		<div className='flex flex-col space-y-1'>
			{isLoading ? (
				<div>Loading</div>
			) : (
				alerts?.map((id: string) => {
					const alert = alertAnotations?.find(
						(anotations) => anotations.id === id
					)

					return (
						alert && (
							<Badge
								key={id}
								size='sm'
								className='w-fit'
								variant={getAlertVariant(alert.level)}
							>
								<p className='overflow-hidden text-nowrap text-ellipsis'>
									{t(alert.name, { defaultValue: alert.name })}
								</p>
							</Badge>
						)
					)
				})
			)}
		</div>
	)
}
